import { useSyncExternalStore } from 'react'
import { candidates as seed, STAGES, type Candidate, type Stage } from './mocks'

// A tiny, dependency-free shared store so candidate stage changes made on one
// screen (e.g. the candidate profile) are instantly reflected everywhere else
// (pipeline, candidates list). Mock-only — no backend.

let state: Candidate[] = seed.map((c) => ({ ...c }))
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

function getSnapshot() {
  return state
}

const STAGE_ORDER: Stage[] = STAGES.map((s) => s.key)

export function stageLabel(stage: Stage): string {
  return STAGES.find((s) => s.key === stage)?.label ?? stage
}

/**
 * Move a candidate to the next stage. Returns the new stage, or null if the
 * candidate is already at the final stage (so the caller can message correctly).
 */
export function advanceCandidateStage(id: string): Stage | null {
  let nextStage: Stage | null = null
  state = state.map((c) => {
    if (c.id !== id) return c
    const idx = STAGE_ORDER.indexOf(c.stage)
    if (idx < 0 || idx >= STAGE_ORDER.length - 1) return c
    nextStage = STAGE_ORDER[idx + 1]
    return { ...c, stage: nextStage }
  })
  if (nextStage) emit()
  return nextStage
}

export function useCandidates(): Candidate[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function useCandidate(id: string): Candidate | undefined {
  return useCandidates().find((c) => c.id === id)
}
